import { IToken, NetworkType, tokenMap } from "@/config";
import { ThemeMode } from "@spheron/ui-library";
import { ethers } from "ethers";
import dayjs from "dayjs";
import { uniqueNamesGenerator } from "unique-names-generator";
import nouns from "./nounDictionary";
import adjectives from "./adjectiveDictionary";
import Regions from "@/config/region-config";
import Cookies from "js-cookie";
import { INode } from "@/app/api/cache/route";

export const isValidEthereumAddress = (address: string): boolean =>
  ethers.isAddress(address);

export const truncateEnd = (text: string, truncateLength: number = 5) => {
  if (text.length > 12) {
    return `${text.slice(0, text.length - truncateLength)}....`;
  }
  return text;
};

export const truncateText = (text: string, maxLength: number): string =>
  text.length > maxLength ? text.slice(0, maxLength) + "..." : text;

export const truncateAddress = (
  address: string,
  substringLength: number = 4
): string =>
  address.length <= 8
    ? address
    : `${address.substring(0, substringLength)}...${address.substring(
        address.length - substringLength
      )}`;

export const setGlobalTheme = (theme: ThemeMode) => {
  const root = window.document.documentElement;
  root.classList.remove(String(ThemeMode.DARK));
  root.classList.remove(String(ThemeMode.LIGHT));
  root.classList.add(theme);
  root.setAttribute("data-theme", theme);
};

export const copyToClipboard = async (copyText: string) => {
  if (typeof window !== "undefined") {
    await navigator.clipboard.writeText(copyText);
  }
};

export const convertEarningsToDisplay = (earning: string | number) => {
  const divisor = 10 ** 18;
  return Number(earning) / divisor;
};

export const getOrdinalSuffix = (num: number): string => {
  const suffixes = ["th", "st", "nd", "rd"];
  const v = num % 100;
  return num + (suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0]);
};

export const getFizzNodeStatusFromStatusId = (statusId: number) => {
  switch (statusId) {
    case 1:
      return "registered";
    case 2:
      return "active";
    case 2.1:
      return "upgrade";
    default:
      return "inactive";
  }
};

export const titleCase = (str: string) => {
  return str.replace(
    /\w\S*/g,
    (text) => text.charAt(0).toUpperCase() + text.substring(1).toLowerCase()
  );
};

export const getTokenDetails = (
  tokenAddress: string,
  networkType: NetworkType
): IToken | undefined => {
  return (
    tokenMap[networkType]?.find(
      (token) => token.address.toLowerCase() === tokenAddress.toLowerCase()
    ) || tokenMap[networkType][0]
  );
};

export const GpuTiers: {
  [x: string]: {
    multiplier: number;
    tier: number;
  };
} = {
  "Entry 1": {
    multiplier: 0.1,
    tier: 10,
  },
  "Entry 2": {
    multiplier: 0.25,
    tier: 9,
  },
  "Low 1": {
    multiplier: 0.5,
    tier: 8,
  },
  "Low 2": {
    multiplier: 0.75,
    tier: 7,
  },
  "Medium 1": {
    multiplier: 1,
    tier: 6,
  },
  "Medium 2": {
    multiplier: 1.5,
    tier: 5,
  },
  "High 1": {
    multiplier: 2,
    tier: 4,
  },
  "High 2": {
    multiplier: 3,
    tier: 3,
  },
  "Ultra High 1": {
    multiplier: 6,
    tier: 2,
  },
  "Ultra High 2": {
    multiplier: 12,
    tier: 1,
  },
};

export const TrustTierMultiplier: { [x: number]: number } = {
  1: 20,
  2: 17,
  3: 15,
  4: 12,
  5: 11,
  6: 10,
  7: 0,
};

export const getTierHoverText = (tier: string, multiplier: number): string => {
  return `🙌 You've earned tier ${tier} with ${multiplier}x multiplier`;
};

export const calculateDaysBetweenTimestamps = (
  timestamp1: string | number,
  timestamp2: string | number
): number => {
  const date1 = dayjs(timestamp1);
  const date2 = dayjs(timestamp2);
  const daysDifference = Math.floor(date2.diff(date1, "day", true));
  return daysDifference;
};

export const getUSDAmount = (
  livePrice: number | null,
  amount: number
): string => {
  if (livePrice) {
    return `${(livePrice * Number(amount)).toFixed(6)}`;
  }
  return "";
};

export const mapTokenToId = (token: string) => {
  const tokens = new Map<string, number>();
  tokens.set("USDT", 825);
  tokens.set("WMATIC", 3890);
  tokens.set("WETH", 2396);
  tokens.set("DAI", 4943);
  tokens.set("USDC", 3408);
  tokens.set("AKT", 7431);
  tokens.set("MATIC", 3890);
  tokens.set("SOL", 5426);
  tokens.set("WAVAX", 5805);
  tokens.set("WBNB", 1839);
  tokens.set("tFIL", 2280);
  tokens.set("FIL", 2280);
  tokens.set("USDbC", 3408);
  tokens.set("WMNT", 27614);
  tokens.set("WXDAI", 9021);
  tokens.set("uSPON", 3408);
  return tokens.get(token);
};

export const getProviderStatusFromStatusId = (statusId: number) => {
  switch (statusId) {
    case 1:
      return "registered";
    case 2:
      return "active";
    default:
      return "inactive";
  }
};

export const compareVersions = (version1: string, version2: string): number => {
  const v1Parts = version1.split(".").map(Number);
  const v2Parts = version2.split(".").map(Number);

  for (let i = 0; i < Math.max(v1Parts.length, v2Parts.length); i++) {
    const v1 = v1Parts[i] || 0;
    const v2 = v2Parts[i] || 0;

    if (v1 > v2) return 1;
    if (v1 < v2) return -1;
  }

  return 0;
};

export const generateUniqueName = (seed: string) => {
  return uniqueNamesGenerator({
    seed: seed.toLowerCase(),
    separator: "-",
    style: "lowerCase",
    dictionaries: [adjectives, nouns],
  });
};

export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const getRegionCoords = (name?: string) => {
  return Regions.find((r) => r.value === name)?.capitalCoords;
};

export const isMobileDevice = (userAgent: string) => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Windows Phone/i.test(
    userAgent
  );
};

export const getCookie = (key: string) => {
  return Cookies.get(key);
};

export const removeCookie = (key: string) => {
  Cookies.remove(key);
};

export const isAppleResource = (id: string, name: string) => {
  if (Number(id) > 4 || name.toLowerCase().includes("apple")) return true;
  return false;
};

export const sortLeaderboardFizzNodes = (nodes: INode[], regions: string[]) => {
  return [...nodes]
    .filter((node) => regions.includes(node.region))
    .filter((node) => node.region)
    .sort((a, b) => {
      // Sort by active state (active comes first) -> active state = 2
      if (a.status !== b.status) {
        return a.status === "2" ? -1 : 1;
      }

      // First, compare by totalRewardsData
      if (Number(a.totalRewardsData) > Number(b.totalRewardsData)) return -1;
      if (Number(a.totalRewardsData) < Number(b.totalRewardsData)) return 1;

      // If totalRewardsData is equal, compare by perEraRewardData
      if (Number(a.perEraRewardData) > Number(b.perEraRewardData)) return -1;
      if (Number(a.perEraRewardData) < Number(b.perEraRewardData)) return 1;

      // If both are equal, return 0 (no change in order)
      return 0;
    });
};

export const convertGbToHighestUnit = (input: number | string) => {
  const units = ["GB", "TB", "PB", "EB", "ZB"];
  let value = typeof input === "string" ? Number(input) : input;
  let unitIndex = 0;

  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex++;
  }

  const formattedValue = Number.isInteger(value)
    ? value.toString()
    : value.toFixed(2);

  return {
    value: Number(formattedValue),
    unit: units[unitIndex],
  };
};

export const formatNumberWithCommas = (num: number): string => {
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

export const formatNumber = (value: number): string => {
  const suffixes = ["", "K", "M", "B"];
  let i = 0;

  while (value >= 1000 && i < suffixes.length - 1) {
    value /= 1000;
    i++;
  }

  const formattedValue =
    value % 1 === 0 ? Math.round(value) : parseFloat(value.toFixed(1));

  return formattedValue + suffixes[i];
};

export const segregateNumberAndUnit = (
  input: string
): { value: number; unit: string } | null => {
  const match = input?.match(/^(\d+(\.\d+)?)([a-zA-Z]+)$/);

  if (match) {
    return {
      value: parseFloat(match[1]),
      unit: match[3],
    };
  }
  return null;
};

export const parseSpec = (spec: string) => {
  const specParts = spec.split(",");
  if (specParts.length < 8) {
    return { error: "Invalid spec format" };
  }

  const osName = specParts[0];
  const vendor = specParts[1];
  const gpuShortName = specParts[2];
  const gpuUnits = specParts[3] ? parseInt(specParts[3], 10) : null;
  const cpuUnits = parseInt(specParts[4], 10);
  const ramUnits = parseInt(specParts[5], 10);
  const storageUnits = parseInt(specParts[6], 10);
  const region = specParts[7];

  return {
    osName,
    vendor,
    gpuShortName,
    gpuUnits,
    cpuUnits,
    ramUnits,
    storageUnits,
    region,
  };
};

export const fetchWithRedirect = async (...args: Parameters<typeof fetch>) => {
  const response = await fetch(...args);
  const statusCode = response.status;
  console.log({ statusCode });
  if (response.status === 401) {
    window.location.href = `https://app.spheron.network/login?redirectUrl=${encodeURIComponent(
      "https://fizz.spheron.network"
    )}`;
  }
  return response;
};
